import { useMemo, useState } from 'react';
import {
  CATEGORIES,
  SETTINGS,
  settingsVisible,
  type SettingDef,
  type Visibility,
} from '../settings/schema';
import { BUILTIN_PRESETS, loadUserPresets, saveUserPreset, deleteUserPreset } from '../settings/presets';
import { store, useAppStore } from '../store/appStore';

function SettingRow({
  def,
  value,
  onChange,
}: {
  def: SettingDef;
  value: string | number | boolean;
  onChange: (v: string | number | boolean) => void;
}) {
  if (def.type === 'boolean') {
    return (
      <label className="setting-row">
        <span className="setting-label" title={def.description}>
          {def.label}
        </span>
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
      </label>
    );
  }
  if (def.type === 'enum') {
    return (
      <label className="setting-row">
        <span className="setting-label" title={def.description}>
          {def.label}
        </span>
        <select value={String(value)} onChange={(e) => onChange(e.target.value)}>
          {def.options?.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    );
  }
  if (def.type === 'color') {
    return (
      <label className="setting-row">
        <span className="setting-label">{def.label}</span>
        <input type="color" value={String(value)} onChange={(e) => onChange(e.target.value)} />
      </label>
    );
  }
  return (
    <label className="setting-row">
      <span className="setting-label" title={def.description}>
        {def.label}
        {def.unit ? <em className="unit"> {def.unit}</em> : null}
      </span>
      <input
        type="number"
        value={Number(value)}
        min={def.min}
        max={def.max}
        step={def.step ?? 1}
        onChange={(e) =>
          onChange(def.type === 'integer' ? parseInt(e.target.value, 10) : parseFloat(e.target.value))
        }
      />
    </label>
  );
}

export function SettingsPanel() {
  const settings = useAppStore((s) => s.settings);
  const visibility = useAppStore((s) => s.visibility);
  const selectedId = useAppStore((s) => s.selectedId);
  const objects = useAppStore((s) => s.objects);
  const [query, setQuery] = useState('');
  const [userPresets, setUserPresets] = useState(loadUserPresets);
  const selected = objects.find((o) => o.id === selectedId);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SETTINGS.filter((s) => {
      if (!settingsVisible(visibility, s)) return false;
      if (!q) return true;
      return (
        s.label.toLowerCase().includes(q) ||
        s.key.toLowerCase().includes(q) ||
        s.category.toLowerCase().includes(q)
      );
    });
  }, [query, visibility]);

  const byCat = useMemo(() => {
    const map = new Map<string, SettingDef[]>();
    for (const c of CATEGORIES) map.set(c, []);
    for (const s of filtered) map.get(s.category)?.push(s);
    return map;
  }, [filtered]);

  return (
    <aside className="settings-panel" data-testid="settings-panel">
      <header className="panel-header">
        <h2>Settings</h2>
        <div className="vis-toggle" role="tablist">
          {(['basic', 'advanced', 'expert'] as Visibility[]).map((v) => (
            <button
              key={v}
              role="tab"
              className={visibility === v ? 'active' : ''}
              onClick={() => store.setVisibility(v)}
            >
              {v[0].toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
        <input
          className="search"
          placeholder="Search settings…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="settings-search"
        />
        <div className="presets">
          <select
            defaultValue=""
            onChange={(e) => {
              const id = e.target.value;
              const p =
                BUILTIN_PRESETS.find((x) => x.id === id) ||
                userPresets.find((x) => x.id === id);
              if (p) store.setSettings(p.values);
              e.target.value = '';
            }}
          >
            <option value="" disabled>
              Load preset…
            </option>
            <optgroup label="Bambu defaults">
              {BUILTIN_PRESETS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </optgroup>
            {userPresets.length > 0 && (
              <optgroup label="Saved">
                {userPresets.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          <button
            type="button"
            onClick={() => {
              const name = prompt('Preset name?');
              if (!name) return;
              const p = {
                id: `user-${Date.now()}`,
                name,
                values: { ...store.getSnapshot().settings },
              };
              setUserPresets(saveUserPreset(p));
            }}
          >
            Save
          </button>
        </div>
      </header>

      {selected && (
        <section className="per-object">
          <h3>Object overrides · {selected.name}</h3>
          <p className="hint">Infill, walls, layer height, supports</p>
          {SETTINGS.filter((s) => s.perObject).map((def) => {
            const overridden = def.id in selected.overrides;
            const value = overridden ? selected.overrides[def.id] : settings[def.id];
            return (
              <div key={def.id} className={`override-row ${overridden ? 'on' : ''}`}>
                <SettingRow
                  def={def}
                  value={value}
                  onChange={(v) => store.setObjectOverride(selected.id, def.id, v)}
                />
                {overridden && (
                  <button
                    type="button"
                    className="linkish"
                    onClick={() => store.setObjectOverride(selected.id, def.id, undefined)}
                  >
                    Reset
                  </button>
                )}
              </div>
            );
          })}
        </section>
      )}

      <div className="settings-tree">
        {[...byCat.entries()].map(([cat, list]) =>
          list.length === 0 ? null : (
            <details key={cat} open={cat === 'Quality' || cat === 'Supports' || !!query}>
              <summary>
                {cat} <span className="count">{list.length}</span>
              </summary>
              <div className="setting-list">
                {list.map((def) => (
                  <SettingRow
                    key={def.id}
                    def={def}
                    value={settings[def.id]}
                    onChange={(v) => store.setSetting(def.id, v)}
                  />
                ))}
              </div>
            </details>
          )
        )}
      </div>

      {userPresets.length > 0 && (
        <footer className="preset-manage">
          {userPresets.map((p) => (
            <button
              key={p.id}
              type="button"
              className="linkish"
              onClick={() => setUserPresets(deleteUserPreset(p.id))}
            >
              Delete “{p.name}”
            </button>
          ))}
        </footer>
      )}
    </aside>
  );
}
